import ShortCourseApi from '@web/api/modules/ShortCourse'
import Logger from '@web/common/Logger'
import tags from '@web/consts/tags'
import {
  IShortCourse,
  IShortCourseTag,
  IShortCourseFilters,
  ICacheEntry,
  ShortCourseState,
  ShortCourseGettersTree,
  ShortCourseMutationsTree,
  ShortCourseActionsTree
} from '@web/store/types/modules/shortCourse'

function createInitialState (): ShortCourseState {
  return {
    courses: {},
    filters: null,
    activeTags: [],
    searchQuery: {
      textSearch: '',
      tagId: undefined,
      cids: [],
      page: 1,
      perPage: 8
    },
    pagination: {
      currentPage: 1,
      perPage: 8,
      totalCount: 0,
      pageCount: 0
    },
    isSearching: false,
    isLoaded: false,
    error: null,
    searchCache: {}
  }
}

function enrichTag (tag: IShortCourseTag): IShortCourseTag {
  const defaultTag = tags[tag.id as keyof typeof tags]
  return {
    ...tag,
    icon: tag.icon || defaultTag?.icon || '',
    sort: tag.sort ?? defaultTag?.sort
  }
}

function intHeader (headers: Headers, name: string, fallback: number): number {
  const raw = headers.get(name)
  if (!raw) return fallback
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : fallback
}

const state: ShortCourseState = createInitialState()

const getters: ShortCourseGettersTree = {
  getCourses: (state) => state.courses,
  getAllCoursesArray: (state) => Object.values(state.courses),
  getCourseById: (state) => (id: number) => state.courses[id],
  getCourseByIds: (state) => (ids: number[]) =>
    ids.map(id => state.courses[id]).filter((course): course is IShortCourse => course !== undefined),
  getCoursesByTags: (state) => {
    function getTagKey (tag: IShortCourseTag) {
      return `${tag.sort}-${tag.id}`
    }
    const coursesByTags: Record<string, { tag: IShortCourseTag; courses: IShortCourse[] }> = {}

    const sortedTags = [...state.activeTags].sort((a, b) => a.sort - b.sort)
    sortedTags.forEach(tag => {
      coursesByTags[getTagKey(tag)] = {
        tag,
        courses: Object.values(state.courses).filter(course => course.tags.some(t => t.id === tag.id || t.name === tag.name))
      }
    })
    return coursesByTags
  },
  getCoursesByTag: (state) => (tagId: number) =>
    Object.values(state.courses).filter(course => {
      const allTags = state.filters?.tags
      const currentTag = allTags?.find(tag => tag.id === tagId)
      return course.tags.some(tag => tag.id === currentTag?.id || tag.name === currentTag?.name)
    }),
  getFilters: (state) => state.filters,
  getTags: (state) => state.filters?.tags ? [...state.filters.tags].sort((a, b) => a.sort - b.sort) : [],
  getActiveTags: (state) => state.activeTags ? [...state.activeTags].sort((a, b) => a.sort - b.sort) : [],
  getSearchQuery: (state) => state.searchQuery,
  getPagination: (state) => state.pagination,
  getIsSearching: (state) => state.isSearching,
  getIsLoaded: (state) => state.isLoaded,
  getError: (state) => state.error,
  getCachedSearch: (state) => (params: {
    textSearch?: string
    tagId?: number
    cids?: number[]
    page?: number
    perPage?: number
  }): ICacheEntry | undefined => {
    const key = generateCacheKey(params)
    if (!key) return undefined
    return state.searchCache[key]
  }
}

const mutations: ShortCourseMutationsTree = {
  setCourses (state, payload) {
    const coursesObj: { [id: number]: IShortCourse } = {}
    payload.forEach(course => {
      coursesObj[course.id] = course
    })
    state.courses = {
      ...state.courses,
      ...coursesObj
    }
  },

  clearCourses (state) {
    state.courses = {}
  },

  setCourse (state, payload) {
    const course = state.courses[payload.id] || {}
    state.courses[payload.id] = {
      ...course,
      ...payload
    }
  },

  removeCourse (state, payload) {
    const { [payload]: _removed, ...rest } = state.courses
    state.courses = rest
  },

  setFilters (state, payload) {
    if (!payload) return
    state.filters = {
      ...state.filters,
      ...payload,
      tags: (payload.tags ?? []).map(enrichTag)
    }
  },

  setActiveTags (state, payload) {
    state.activeTags = payload.map(enrichTag)
  },

  addActiveTags (state, payload) {
    const newActiveTags = payload.map(enrichTag)
    const allTags = [...state.activeTags, ...newActiveTags]
    state.activeTags = allTags.filter((tag, index, self) =>
      index === self.findIndex(c => c.id === tag.id)
    )
  },

  setSearchQuery (state, payload) {
    state.searchQuery = payload
  },

  setPagination (state, payload) {
    state.pagination = payload
  },

  setIsSearching (state, payload) {
    state.isSearching = payload
  },

  setIsLoaded (state, payload) {
    state.isLoaded = payload
  },

  setError (state, payload) {
    state.error = payload
  },

  setCacheEntry (state, payload) {
    state.searchCache = {
      ...state.searchCache,
      [payload.key]: payload.entry
    }
  },

  removeCacheEntry (state, payload) {
    const { [payload]: _removed, ...rest } = state.searchCache
    state.searchCache = rest
  },

  clearSearchCache (state) {
    state.searchCache = {}
    Logger.info('Search cache cleared')
  },

  logout (state) {
    Object.assign(state, createInitialState())
  }
}

/**
 * Генерирует ключ кэша на основе всех параметров поискового запроса.
 * Возвращает null, если textSearch пустой (кэшируем только текстовые поиски).
 */
function generateCacheKey (params: {
  textSearch?: string
  tagId?: number
  cids?: number[]
  page?: number
  perPage?: number
}): string | null {
  if (!params.textSearch || params.textSearch.trim() === '') {
    return null
  }
  const cids = (params.cids ?? []).slice().sort((a, b) => a - b).join(',')
  return JSON.stringify({
    q: params.textSearch.trim(),
    tagId: params.tagId ?? null,
    cids,
    page: params.page ?? 1,
    perPage: params.perPage ?? 8
  })
}

const actions: ShortCourseActionsTree = {
  /**
   * Поиск курсов с сохранением параметров запроса
   */
  async search ({ state, commit }, payload) {
    const {
      textSearch,
      tagId,
      cids = [],
      page = 1,
      perPage = 8,
      clearPrevious = false,
      forceRefresh = false
    } = payload

    commit('setIsSearching', true)
    commit('setError', null)

    // Сохраняем параметры запроса
    commit('setSearchQuery', { textSearch, tagId, cids, page, perPage })

    // Генерируем ключ кэша (textSearch + tagId + cids + page + perPage)
    const cacheKey = generateCacheKey({ textSearch, tagId, cids, page, perPage })

    try {
      let data: { courses: IShortCourse[]; filters?: IShortCourseFilters }
      let headers: Headers

      // Проверяем наличие данных в кэше (если есть cacheKey и не forceRefresh)
      const cachedEntry = cacheKey && !forceRefresh ? state.searchCache[cacheKey] : undefined

      if (cachedEntry) {
        // Используем данные из кэша
        Logger.info('Using cached search results for:', cacheKey)
        data = cachedEntry.data
        headers = cachedEntry.headers
      } else {
        // Выполняем API запрос
        if (forceRefresh && cacheKey) {
          Logger.info('Force refreshing search results for:', cacheKey)
        }

        const response = await ShortCourseApi.search({
          textSearch,
          tagId,
          cids,
          page,
          perPage
        })

        data = response.data
        headers = response.headers

        // Сохраняем результат в кэш (только если есть textSearch)
        if (cacheKey) {
          commit('setCacheEntry', {
            key: cacheKey,
            entry: {
              data,
              headers,
              timestamp: Date.now()
            }
          })
          Logger.info('Search results cached for:', cacheKey)
        }
      }

      // Если clearPrevious = true, очищаем предыдущие результаты
      if (clearPrevious) {
        commit('clearCourses')
      }

      // Сохраняем фильтры
      const filters = data.filters
      commit('setFilters', filters)

      const activeTags: IShortCourseTag[] = []

      // Сохраняем курсы (учитываем дубли)
      data.courses.forEach(course => {
        commit('setCourse', course)

        course.tags.forEach(_tag => {
          if (_tag.id && _tag.name && !activeTags.some(t => t.id === _tag.id)) {
            const tag = filters?.tags.find(t => _tag && t.id === _tag.id)
            if (tag) {
              activeTags.push(tag)
            }
          }
        })
      })

      commit('setActiveTags', activeTags)

      commit('setPagination', {
        currentPage: intHeader(headers, 'x-pagination-current-page', 1),
        pageCount: intHeader(headers, 'x-pagination-page-count', 0),
        perPage: intHeader(headers, 'x-pagination-per-page', 8),
        totalCount: intHeader(headers, 'x-pagination-total-count', 0)
      })

      commit('setIsLoaded', true)
      return data
    } catch (error) {
      Logger.error('Failed to search courses:', error)
      commit('setError', 'Failed to search courses')
      throw error
    } finally {
      commit('setIsSearching', false)
    }
  },

  async searchByCourseId ({ getters, commit }, { courseId, force = false }) {
    const cached = getters.getCourseById(courseId)
    // Запрашиваем, если курса ещё нет, оффер по нему ещё не запрашивали
    // (offer === undefined) или явно просят обновить. offer === null означает
    // «запросили, оффера нет» — повторно не ходим без force.
    if (cached && cached.offer !== undefined && !force) {
      return cached
    }

    try {
      const response = await ShortCourseApi.search({
        cids: [courseId],
        page: 1,
        perPage: 1,
        withOffer: true
      })
      const course = response.data.courses[0]
      if (!course) {
        throw new Error(`Course not found: ${courseId}`)
      }

      commit('setCourse', course)

      const activeTags: IShortCourseTag[] = []
      course.tags.forEach(tag => {
        if (tag.id && tag.name && !activeTags.some(t => t.id === tag.id)) {
          activeTags.push(tag)
        }
      })
      commit('addActiveTags', activeTags)

      return course
    } catch (error) {
      Logger.error('Failed to search course by id:', error)
      throw error
    }
  },

  /**
   * Поиск с текущими параметрами (для обновления)
   */
  async refreshSearch (context) {
    return await context.dispatch('search', {
      ...context.state.searchQuery,
      clearPrevious: true,
      forceRefresh: true
    })
  },

  /**
   * Загрузить следующую страницу результатов
   */
  async loadNextPage (context) {
    const nextPage = context.state.searchQuery.page + 1
    return await context.dispatch('search', {
      ...context.state.searchQuery,
      page: nextPage,
      clearPrevious: false
    })
  },

  /**
   * Поиск с новыми параметрами (очищает предыдущие результаты)
   */
  async searchWithNewParams (context, payload) {
    return await context.dispatch('search', {
      ...payload,
      clearPrevious: true
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any
